import * as React from 'react';
import {
  Icon,
  SelectionOption,
  Text,
  useLightbox,
} from '@quality24/design-system';
import classNames from 'classnames';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import styles from './MultipleChoiceWithQuantity.module.scss';

export interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  value: string | number;
  selected?: boolean;
  onClick?: React.MouseEventHandler<Element>;
  title?: string;
  subtitle?: string;
  image?: string;
  children?: React.ReactNode;
}

const ListItem: React.FunctionComponent<Props> = ({
  className,
  title,
  subtitle,
  image,
  children,
  value,
  selected,
  onClick,
  ...htmlElementProps
}) => {
  const { createLightbox, destroyLightbox } = useLightbox();

  const handleClickImage = (
    event: React.MouseEvent,
    imageUrl: Props['image'],
  ) => {
    event.stopPropagation();

    if (!imageUrl) return;

    const lightboxId = `image-lightbox-${Date.now()}`;
    createLightbox({
      id: lightboxId,
      fitContent: true,
      onHide: () => destroyLightbox(lightboxId),
      children: <img src={imageUrl} alt={title || 'Image'} />,
    });
  };

  return (
    <SelectionOption
      value={value}
      selected={selected}
      onClick={onClick}
      aria-label={title}
      className={classNames(styles['list-item'], className)}
      {...htmlElementProps}
    >
      {image && (
        <img
          src={image}
          alt={title}
          role="presentation"
          className={classNames(styles.image, 'rounded-md mb-2')}
          onClick={(event) => handleClickImage(event, image)}
        />
      )}
      <div className={styles['title-wrapper']}>
        <Text as="span" className="d-flex flex-column" weight="semiBold">
          {title}
          {subtitle && (
            <Text as="span" color="gray400">
              {subtitle}
            </Text>
          )}
        </Text>
        {selected && <Icon icon={faCheck} />}
      </div>
      <div className="d-flex flex-column align-items-center">{children}</div>
    </SelectionOption>
  );
};

export default ListItem;
